/**
 * 批量事务抽屉 - 常量定义
 * 统一管理所有常量，提高代码可维护性
 */

// 数据加载状态
export const DATA_LOADING_STATUS = {
    IDLE: "IDLE",
    LOADING: "LOADING",
    SUCCESS: "SUCCESS",
    ERROR: "ERROR",
};

// 工作流模式
export const WORKFLOW_MODE = {
    CREATE: "CREATE",
    EDIT: "EDIT",
    MIXED: "MIXED",
};

// 客户端状态枚举
export const CLIENT_STATUS = {
    PRISTINE: "pristine", // 从服务器加载的初始状态，用户未做任何修改
    NEW: "new", // 用户在客户端创建的新明细条目，数据库中尚不存在
    MODIFIED: "modified", // 用户编辑过的一个 pristine 条目
    DELETED: "deleted", // 用户标记为删除的 pristine 或 modified 条目
};

// 事务类型映射 - 统一使用大写格式
export const TRANSACTION_TYPES = {
    PERMIT_REQUEST: "PERMIT_REQUEST",
    FUEL_REQUEST: "FUEL_REQUEST",
    CATERING_ORDER: "CATERING_ORDER",
    CREW_TRANSPORTATION: "CREW_TRANSPORTATION",
    HOTAC: "HOTAC",
    FLIGHT_BRIEF: "FLIGHT_BRIEF",
};

// 支持的事务类型（当前可用）
export const SUPPORTED_TRANSACTION_TYPES = {
    PERMIT_REQUEST: "PERMIT_REQUEST",
    FUEL_REQUEST: "FUEL_REQUEST",
    CATERING_ORDER: "CATERING_ORDER",
    CREW_TRANSPORTATION: "CREW_TRANSPORTATION",
    HOTAC: "HOTAC",
    FLIGHT_BRIEF: "FLIGHT_BRIEF",
};

// 事务类型到表名的映射（后端数据库表名）
export const TRANSACTION_TYPE_TO_TABLE = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "permit_request",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel_request",
    [TRANSACTION_TYPES.CATERING_ORDER]: "catering_order",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew_transportation",
    [TRANSACTION_TYPES.HOTAC]: "hotac",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight_brief",
};

// 事务类型到明细表名的映射（后端数据库明细表名）
export const TRANSACTION_TYPE_TO_DETAIL_TABLE = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "permit_request_detail",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel_request_detail",
    [TRANSACTION_TYPES.CATERING_ORDER]: "catering_order_detail",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew_transportation_detail",
    [TRANSACTION_TYPES.HOTAC]: "hotac_detail",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight_brief_detail",
};

// 事务类型到服务类型的映射（用于API调用，与 CostType 枚举一致）
export const TRANSACTION_TYPE_TO_SERVICE_TYPE = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "TRAFFIC_RIGHTS_PERMIT",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "FUEL",
    [TRANSACTION_TYPES.CATERING_ORDER]: "CATERING",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "TRANSPORTATION",
    [TRANSACTION_TYPES.HOTAC]: "ACCOMMODATION",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "FLIGHT_BRIEF",
};

// 事务类型显示名称
export const TRANSACTION_TYPE_LABELS = {
    [TRANSACTION_TYPES.PERMIT_REQUEST]: "许可申请",
    [TRANSACTION_TYPES.FUEL_REQUEST]: "燃油申请",
    [TRANSACTION_TYPES.CATERING_ORDER]: "配餐订单",
    [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "机组接送",
    [TRANSACTION_TYPES.HOTAC]: "酒店住宿",
    [TRANSACTION_TYPES.FLIGHT_BRIEF]: "航班简报",
};

// UI相关常量
export const UI_CONSTANTS = {
    // 抽屉宽度
    DRAWER_WIDTH: "80%",

    // 分页大小
    PAGE_SIZE: 20,

    // 防抖延迟
    DEBOUNCE_DELAY: 300,

    // 节流延迟
    THROTTLE_DELAY: 100,

    // 最大文件大小 (MB)
    MAX_FILE_SIZE: 10,

    // 支持的文件类型
    SUPPORTED_FILE_TYPES: [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"],
};

// 验证规则
export const VALIDATION_RULES = {
    // 必填字段
    REQUIRED_FIELDS: {
        TRANSACTION: ["flightId", "supplierId"],
        DETAIL: ["contractItemId", "productId", "quantity", "unitPrice"],
    },

    // 数值范围
    NUMBER_RANGES: {
        QUANTITY: { min: 0, max: 999999 },
        UNIT_PRICE: { min: 0, max: 999999.99 },
    },

    // 字符串长度
    STRING_LENGTHS: {
        REMARKS: { max: 500 },
        PRODUCT_NAME: { max: 100 },
    },
};

// 错误消息
export const ERROR_MESSAGES = {
    // 网络错误
    NETWORK_ERROR: "网络连接失败，请检查网络设置",
    TIMEOUT_ERROR: "请求超时，请稍后重试",
    SERVER_ERROR: "服务器错误，请联系管理员",

    // 验证错误
    REQUIRED_FIELD: "此字段为必填项",
    INVALID_NUMBER: "请输入有效的数字",
    INVALID_EMAIL: "请输入有效的邮箱地址",
    INVALID_PHONE: "请输入有效的电话号码",

    // 业务错误
    NO_FLIGHTS_SELECTED: "请选择至少一个航班",
    NO_AIRPORTS_SELECTED: "请选择至少一个机场",
    NO_SUPPLIER_SELECTED: "请选择供应商",
    NO_DETAILS_ADDED: "请为事务添加至少一条明细",
    DUPLICATE_DETAIL: "不能添加重复的明细项",

    // 文件错误
    FILE_TOO_LARGE: "文件大小不能超过10MB",
    UNSUPPORTED_FILE_TYPE: "不支持的文件类型",
    FILE_UPLOAD_FAILED: "文件上传失败",
};

// 成功消息
export const SUCCESS_MESSAGES = {
    SAVE_SUCCESS: "保存成功",
    SUBMIT_SUCCESS: "提交成功",
    DELETE_SUCCESS: "删除成功",
    UPLOAD_SUCCESS: "上传成功",
    COPY_SUCCESS: "复制成功",
};

// API端点
export const API_ENDPOINTS = {
    INITIALIZE_DATA: "/v1/batch-transaction-v4/initialize-data",
    BATCH_PROCESS: "/v1/batch-transaction-v4/batch-process",
    AVAILABLE_PRODUCTS: "/v1/batch-transactions/available-contract-items",
    SUPPLIERS: "/v3/contract/business-partners/suppliers",
    FLIGHTS: "/v1/flights",
};

// 本地存储键名
export const STORAGE_KEYS = {
    DRAFT_TRANSACTIONS: "batch_transaction_draft",
    USER_PREFERENCES: "batch_transaction_preferences",
    RECENT_SUPPLIERS: "recent_suppliers",
    RECENT_AIRPORTS: "recent_airports",
};

// 事件名称
export const EVENT_NAMES = {
    TRANSACTION_CREATED: "transaction_created",
    TRANSACTION_UPDATED: "transaction_updated",
    TRANSACTION_DELETED: "transaction_deleted",
    DETAIL_ADDED: "detail_added",
    DETAIL_UPDATED: "detail_updated",
    DETAIL_DELETED: "detail_deleted",
    BATCH_SUBMITTED: "batch_submitted",
};

// 默认值
export const DEFAULT_VALUES = {
    TRANSACTION: {
        status: "PENDING",
        remarks: "",
        additionalData: null,
    },
    DETAIL: {
        quantity: 1,
        currency: "USD",
        status: "PENDING",
        remarks: "",
    },
    PAGINATION: {
        current: 1,
        pageSize: UI_CONSTANTS.PAGE_SIZE,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
    },
};

// 颜色主题
export const THEME_COLORS = {
    PRIMARY: "#1890ff",
    SUCCESS: "#52c41a",
    WARNING: "#faad14",
    ERROR: "#ff4d4f",
    INFO: "#1890ff",

    // 状态颜色
    STATUS_COLORS: {
        PENDING: "#faad14",
        APPROVED: "#52c41a",
        REJECTED: "#ff4d4f",
        CANCELLED: "#d9d9d9",
    },

    // 客户端状态颜色
    CLIENT_STATUS_COLORS: {
        [CLIENT_STATUS.PRISTINE]: "#d9d9d9",
        [CLIENT_STATUS.NEW]: "#52c41a",
        [CLIENT_STATUS.MODIFIED]: "#faad14",
        [CLIENT_STATUS.DELETED]: "#ff4d4f",
    },
};

// 图标映射
export const ICON_MAPPING = {
    TRANSACTION_TYPES: {
        [TRANSACTION_TYPES.PERMIT_REQUEST]: "FileProtectOutlined",
        [TRANSACTION_TYPES.FUEL_REQUEST]: "ThunderboltOutlined",
        [TRANSACTION_TYPES.CATERING_ORDER]: "ShopOutlined",
        [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "CarOutlined",
        [TRANSACTION_TYPES.HOTAC]: "HomeOutlined",
        [TRANSACTION_TYPES.FLIGHT_BRIEF]: "FileTextOutlined",
    },

    CLIENT_STATUS: {
        [CLIENT_STATUS.PRISTINE]: "MinusOutlined",
        [CLIENT_STATUS.NEW]: "PlusOutlined",
        [CLIENT_STATUS.MODIFIED]: "EditOutlined",
        [CLIENT_STATUS.DELETED]: "DeleteOutlined",
    },

    ACTIONS: {
        ADD: "PlusOutlined",
        EDIT: "EditOutlined",
        DELETE: "DeleteOutlined",
        VIEW: "EyeOutlined",
        COPY: "CopyOutlined",
        DOWNLOAD: "DownloadOutlined",
        UPLOAD: "UploadOutlined",
        REFRESH: "ReloadOutlined",
        SEARCH: "SearchOutlined",
        FILTER: "FilterOutlined",
        SORT: "SortAscendingOutlined",
        EXPAND: "ExpandAltOutlined",
        COLLAPSE: "ShrinkOutlined",
    },
};
